// Controller for Rapid Reload settings: product selection, payment readiness,
// persistence, and analytics.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useProductPaymentMethods } from './useProductPaymentMethods';
import { useUserStripePortal } from './useUserStripePortal';
import { useLoonProducts } from '../transport/cache';
import { analyticsService } from '../services/analyticsService';
import { updateUser } from '../transport/api/protectedApi';
import type {
  AnalyticsEventParams,
  AnalyticsSurface,
} from '../types/analytics';
import type { ProductDocument } from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('useProductRapidReload');

export const RAPID_RELOAD_NONE_VALUE = '__none__';

type RapidReloadPaymentMethodStatusState =
  'loading' | 'success' | 'unavailable' | 'missing';

interface RapidReloadPaymentMethodStatus {
  state: RapidReloadPaymentMethodStatusState;
  message: string;
}

interface UseProductRapidReloadOptions {
  onClose: () => void;
  onSavingChange?: (isSaving: boolean) => void;
  knownHasSavedMethod?: boolean;
  analyticsSurface?: AnalyticsSurface;
  portalReturnUrl?: string;
}

interface UseProductRapidReloadResult {
  products: ProductDocument[];
  productsLoading: boolean;
  productsError: string | null;
  selectedRapidReloadProduct: ProductDocument | null;
  selectedRapidReloadPriceId: string | null;
  toggleGroupValue: string;
  rapidReloadError: string | null;
  isSavingRapidReload: boolean;
  billingPortalUrl: string | null;
  portalLinkLoading: boolean;
  paymentMethodStatus: RapidReloadPaymentMethodStatus;
  selectionBlockedMessage: string | null;
  canConfirmRapidReload: boolean;
  handlePortalLinkClick: () => void;
  handleToggleValueChange: (next: string) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
}

const getRapidReloadProductAnalytics = (
  product: ProductDocument | null
): Pick<
  AnalyticsEventParams,
  'product_name' | 'loons_granted' | 'list_price' | 'currency'
> => ({
  product_name: product?.name,
  loons_granted: product?.loonsGranted ?? undefined,
  list_price: product?.listPrice,
  currency: product?.currency,
});

export function useProductRapidReload({
  onClose,
  onSavingChange,
  knownHasSavedMethod,
  analyticsSurface = 'library_rapid_reload',
  portalReturnUrl = '/library?dialog=rapid-reload',
}: UseProductRapidReloadOptions): UseProductRapidReloadResult {
  const { user, setUser, refetchUser } = useAuth();
  const {
    products,
    loading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useLoonProducts({ autoFetch: true });
  const {
    hasSavedMethod,
    isFetched: paymentMethodsFetched,
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useProductPaymentMethods({ autoFetch: true });
  const {
    portalUrl: billingPortalUrl,
    loadingPortal: portalLinkLoading,
    trackPortalClick,
  } = useUserStripePortal({
    returnUrl: portalReturnUrl,
    autoFetch: true,
    trackingLabel: 'Open Stripe Customer Portal',
    trackingContext: {
      link_location: analyticsSurface,
      surface: analyticsSurface,
    },
  });

  const [selectedRapidReloadPriceId, setSelectedRapidReloadPriceId] = useState<
    string | null
  >(user?.rapidReload ?? null);
  const [rapidReloadError, setRapidReloadError] = useState<string | null>(null);
  const [isSavingRapidReload, setIsSavingRapidReload] =
    useState<boolean>(false);
  const openTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    setSelectedRapidReloadPriceId(user?.rapidReload ?? null);
    setRapidReloadError(null);
    void refetchProducts();
    void refetchPaymentMethods();
  }, [refetchPaymentMethods, refetchProducts, user?.rapidReload]);

  useEffect(() => {
    onSavingChange?.(isSavingRapidReload);

    return () => {
      onSavingChange?.(false);
    };
  }, [isSavingRapidReload, onSavingChange]);

  const hasSavedPaymentMethod = hasSavedMethod || !!knownHasSavedMethod;
  const paymentMethodStatusResolved =
    !!knownHasSavedMethod || paymentMethodsFetched || !!paymentMethodsError;

  useEffect(() => {
    if (openTrackedRef.current || !paymentMethodStatusResolved) {
      return;
    }

    analyticsService.trackRapidReloadDialogOpened({
      surface: analyticsSurface,
      flow_type: 'rapid_reload_setup',
      stripe_price_id: user?.rapidReload || undefined,
      has_saved_method: hasSavedPaymentMethod,
    });
    openTrackedRef.current = true;
  }, [
    analyticsSurface,
    hasSavedPaymentMethod,
    paymentMethodStatusResolved,
    user?.rapidReload,
  ]);

  const selectedRapidReloadProduct = useMemo(
    () =>
      products.find(
        (product) => product.stripePriceId === selectedRapidReloadPriceId
      ) || null,
    [products, selectedRapidReloadPriceId]
  );

  const packSelectionRequiresSavedPayment =
    selectedRapidReloadPriceId !== null && !hasSavedPaymentMethod;
  const selectionBlockedMessage =
    productsError || !packSelectionRequiresSavedPayment
      ? null
      : 'Please add payment method first';
  const canConfirmRapidReload =
    !isSavingRapidReload &&
    !productsError &&
    !packSelectionRequiresSavedPayment;

  const paymentMethodStatus = useMemo((): RapidReloadPaymentMethodStatus => {
    if (knownHasSavedMethod) {
      return {
        state: 'success',
        message: 'Saved in Stripe',
      };
    }

    if (!paymentMethodsFetched || paymentMethodsLoading) {
      return {
        state: 'loading',
        message: 'Checking payment method...',
      };
    }

    if (paymentMethodsError) {
      return {
        state: 'unavailable',
        message: 'Oops! Status unknown right now',
      };
    }

    if (hasSavedMethod) {
      return {
        state: 'success',
        message: 'Saved in Stripe',
      };
    }

    return {
      state: 'missing',
      message: 'Not saved in Stripe',
    };
  }, [
    hasSavedMethod,
    knownHasSavedMethod,
    paymentMethodsError,
    paymentMethodsFetched,
    paymentMethodsLoading,
  ]);

  const handlePortalLinkClick = () => {
    trackPortalClick();
  };

  const handlePackSelectionChange = (nextPriceId: string | null) => {
    setRapidReloadError(null);
    setSelectedRapidReloadPriceId(nextPriceId);

    const nextProductSelected =
      products.find((product) => product.stripePriceId === nextPriceId) || null;
    analyticsService.trackLoonPackSelected({
      surface: analyticsSurface,
      flow_type: 'rapid_reload_setup',
      stripe_price_id: nextPriceId || undefined,
      ...getRapidReloadProductAnalytics(nextProductSelected),
      has_saved_method: hasSavedPaymentMethod,
    });
  };

  const handleToggleValueChange = (next: string) => {
    if (next === '') {
      return;
    }

    handlePackSelectionChange(next === RAPID_RELOAD_NONE_VALUE ? null : next);
  };

  const handleSave = async () => {
    if (!canConfirmRapidReload) {
      return;
    }

    analyticsService.trackButtonClick('Rapid Reload Confirm', {
      surface: analyticsSurface,
      flow_type: 'rapid_reload_setup',
      stripe_price_id: selectedRapidReloadPriceId || undefined,
      has_saved_method: hasSavedPaymentMethod,
      link_location: analyticsSurface,
    });
    setIsSavingRapidReload(true);
    setRapidReloadError(null);

    try {
      const response = await updateUser({
        rapidReload: selectedRapidReloadPriceId,
      });

      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        await refetchUser();
      }

      if (selectedRapidReloadPriceId) {
        analyticsService.trackRapidReloadSaved({
          surface: analyticsSurface,
          flow_type: 'rapid_reload_setup',
          stripe_price_id: selectedRapidReloadPriceId,
          ...getRapidReloadProductAnalytics(selectedRapidReloadProduct),
          has_saved_method: hasSavedPaymentMethod,
        });
      } else {
        analyticsService.trackRapidReloadDisabled({
          surface: analyticsSurface,
          flow_type: 'rapid_reload_setup',
          has_saved_method: hasSavedPaymentMethod,
        });
      }

      onClose();
    } catch (saveError) {
      logger.error('Failed to save rapid reload settings', saveError);
      analyticsService.trackRapidReloadSaveFailed({
        surface: analyticsSurface,
        flow_type: 'rapid_reload_setup',
        stripe_price_id: selectedRapidReloadPriceId || undefined,
        has_saved_method: hasSavedPaymentMethod,
        error_code: 'rapid_reload_save_failed',
        error_message:
          saveError instanceof Error ? saveError.message : String(saveError),
      });
      setRapidReloadError('Unable to save Rapid Reload settings.');
    } finally {
      setIsSavingRapidReload(false);
    }
  };

  const handleCancel = () => {
    if (isSavingRapidReload) {
      return;
    }

    analyticsService.trackButtonClick('Rapid Reload Dialog Close', {
      surface: analyticsSurface,
      flow_type: 'rapid_reload_setup',
      close_method: 'cancel_button',
      link_location: analyticsSurface,
    });
    onClose();
  };

  const toggleGroupValue =
    selectedRapidReloadPriceId === null
      ? RAPID_RELOAD_NONE_VALUE
      : selectedRapidReloadPriceId;

  return {
    products,
    productsLoading,
    productsError,
    selectedRapidReloadProduct,
    selectedRapidReloadPriceId,
    toggleGroupValue,
    rapidReloadError,
    isSavingRapidReload,
    billingPortalUrl,
    portalLinkLoading,
    paymentMethodStatus,
    selectionBlockedMessage,
    canConfirmRapidReload,
    handlePortalLinkClick,
    handleToggleValueChange,
    handleSave,
    handleCancel,
  };
}
